import {
  NONE,
  YIELD_MAINTENANCE,
  MAKE_WHOLE,
  FLAT_PERCENTAGE,
  DEFEASANCE,
  LOCKOUT,
  YIELD_MAINTENANCE_AGENCY,
  AGGREGATE_INTEREST,
  SPREAD_MAINTENANCE
} from "constants/prepaymentTypes";
import { get } from "lodash";
import interestRateTypes from "constants/interestRateTypes";

import { transformDscr, transformDecimal } from "../../utils/formTransformers";

export const modifyValuesForSerialization = values => {
  if (values.details) {
    modifyDetailsValues(values.details);
  }
  if (values.amortizationDetails) {
    modifyAmortizationDetails(values.amortizationDetails);
  }
  if (values.extensionTerms) {
    modifyExtensionTerms(values.extensionTerms);
  }
  if (values.closingCosts) {
    modifyClosingCosts(values.closingCosts);
  }
  if (values.prepaymentTerms) {
    modifyPrepaymentTerms(values.prepaymentTerms);
  }
  if (values.personalGuarantors) {
    modifyPersonalGuarantors(values.personalGuarantors);
  }
  if (values.companyGuarantors) {
    modifyCompanyGuarantors(values.companyGuarantors);
  }
  if (values.covenants) {
    modifyCovenants(values.covenants);
  }
  if (values.prepaymentSchedules) {
    modifyPrepaymentSchedules(values.prepaymentSchedules);
  }
  if (values.recourse) {
    modifyRecourse(values.recourse);
  }
  if (values.hedgeRequirements) {
    modifyHedgeRequirements(values.hedgeRequirements);
  }
  if (values.hedges) {
    modifyHedges(values.hedges);
  }
  if (values.extensions && values.extensions.length > 0) {
    modifyExtensions(values.extensions);
  }
  if (values.properties && values.properties.length > 0) {
    modifyProperties(values.properties);
  }

  return values;
};

const modifyDetailsValues = details => {
  details.allInRate = details.allInRate ? details.allInRate / 100 : null;
  if (get(details, "rateTypeId") === interestRateTypes.FIXED) {
    details.interestRate = details.interestRate !== undefined && details.interestRate !== null ? details.interestRate / 100 : null
    details.amortizationRate = "";
    details.loanSpread = details.loanSpread !== undefined && details.loanSpread !== null ? details.loanSpread / 100 : null;
    details.spreadReductionTerms = "";
    details.lookbackDays = "";
    details.roundingId = "";
    details.indexFloor = details.indexFloor ? details.indexFloor / 100 : null;
    details.embeddedCap = "";
    details.defaultInterestSpread = details.defaultInterestSpread ? details.defaultInterestSpread / 100 : null;
  } else {
    details.loanSpread = details.loanSpread !== undefined && details.loanSpread !== null ? details.loanSpread / 100 : null;
    details.indexFloor = details.indexFloor !== undefined && details.indexFloor !== null ? details.indexFloor / 100 : null;
    details.embeddedCap = details.embeddedCap !== undefined && details.embeddedCap !== null ? details.embeddedCap / 100 : null;
    details.defaultInterestSpread = details.defaultInterestSpread !== undefined && details.defaultInterestSpread !== null  ? details.defaultInterestSpread / 100 : null;
    details.interestRate = "";
    details.rateReductionTerms = "";
  }
};

const modifyAmortizationDetails = amortDetails => {
  amortDetails.amortizationRate = amortDetails.amortizationRate ? amortDetails.amortizationRate / 100 : null;
};

const modifyPrepaymentTerms = prepaymentTerms => {
  prepaymentTerms.exitFee = prepaymentTerms.exitFee ? prepaymentTerms.exitFee / 100 : null;
  prepaymentTerms.securitizationDate = prepaymentTerms.securitizationDate;
};

const modifyPersonalGuarantors = personalGuarantors => {
  if (personalGuarantors) {
    for (let i = 0; i < personalGuarantors.length; i++) {
      const guarantor = personalGuarantors[i];
      guarantor.recoursePercent = guarantor.recoursePercent && guarantor.recoursePercent !== "" ? guarantor.recoursePercent / 100 : null;
      guarantor.recourseAmount = guarantor.recourseAmount && guarantor.recourseAmount !== "" ? guarantor.recourseAmount : null;
      guarantor.leverage = guarantor.leverage ? guarantor.leverage / 100 : null;
      guarantor.burndown = guarantor.burndownRaw ? "1" : "0";
      if (guarantor.guarantorRecourseSchedules && guarantor.guarantorRecourseSchedules.length > 0) {
        const guarantorRecourseSchedules = [];
        for (let index = 0; index < guarantor.guarantorRecourseSchedules.length; index++) {
          const schedule = modifyGuarantorRecourseSchedule(guarantor.guarantorRecourseSchedules[index]);
          guarantorRecourseSchedules.push(schedule);
        }
        guarantor.guarantorRecourseSchedules = guarantorRecourseSchedules;
      }
    }
  }
};

const modifyCompanyGuarantors = companyGuarantors => {
  if (companyGuarantors) {
    for (let i = 0; i < companyGuarantors.length; i++) {
      const guarantor = companyGuarantors[i];
      guarantor.contactId = null;
      guarantor.recoursePercent = guarantor.recoursePercent && guarantor.recoursePercent !== "" ? guarantor.recoursePercent / 100 : null;
      guarantor.recourseAmount = guarantor.recourseAmount && guarantor.recourseAmount !== "" ? guarantor.recourseAmount : null;
      guarantor.leverage = guarantor.leverage ? guarantor.leverage / 100 : null;
      guarantor.burndown = guarantor.burndownRaw ? "1" : "0";
      if (guarantor.guarantorRecourseSchedules && guarantor.guarantorRecourseSchedules.length > 0) {
        const guarantorRecourseSchedules = [];
        for (let index = 0; index < guarantor.guarantorRecourseSchedules.length; index++) {
          const schedule = modifyGuarantorRecourseSchedule(guarantor.guarantorRecourseSchedules[index]);
          guarantorRecourseSchedules.push(schedule);
        }
        guarantor.guarantorRecourseSchedules = guarantorRecourseSchedules;
      }
    }
  }
};

const modifyExtensionTerms = extensionTerms => {
  extensionTerms.extensionOptions = transformDecimal(extensionTerms.extensionOptions);
  extensionTerms.extensionTermMonths = transformDecimal(extensionTerms.extensionTermMonths);
  extensionTerms.extensionRemaining = transformDecimal(extensionTerms.extensionRemaining);
  extensionTerms.extensionFeeAmt = transformDecimal(extensionTerms.extensionFeeAmt);
  extensionTerms.extensionFeePercent = extensionTerms.extensionFeePercent ? extensionTerms.extensionFeePercent / 100 : null;
  extensionTerms.dscrReq = transformDscr(extensionTerms.dscrReq);
  extensionTerms.ltvReq = extensionTerms.ltvReq ? extensionTerms.ltvReq / 100 : null;
  extensionTerms.debtYieldReq = extensionTerms.debtYieldReq ? extensionTerms.debtYieldReq / 100 : null;
};

const modifyClosingCosts = closingCosts => {
  closingCosts.brokerFeePercent = closingCosts.brokerFeePercent ? closingCosts.brokerFeePercent / 100 : null;
  closingCosts.originationFeePercent = closingCosts.originationFeePercent ? closingCosts.originationFeePercent / 100 : null;
  closingCosts.otherFeePercent = closingCosts.otherFeePercent ? closingCosts.otherFeePercent / 100 : null;

  closingCosts.originationFeeAmount = transformDecimal(closingCosts.originationFeeAmount);
  closingCosts.brokerFeeAmount = transformDecimal(closingCosts.brokerFeeAmount);
  closingCosts.lenderLegalFeeAmount = transformDecimal(closingCosts.lenderLegalFeeAmount);
  closingCosts.borrowerLegalFeeAmount = transformDecimal(closingCosts.borrowerLegalFeeAmount);
  closingCosts.inspectionFeeAmount = transformDecimal(closingCosts.inspectionFeeAmount);
  closingCosts.appraisalFeeAmount = transformDecimal(closingCosts.appraisalFeeAmount);
  closingCosts.rateLockFeeAmount = transformDecimal(closingCosts.rateLockFeeAmount);
  closingCosts.hedgeFeeAmount = transformDecimal(closingCosts.hedgeFeeAmount);
  closingCosts.titleAmount = transformDecimal(closingCosts.titleAmount);
  closingCosts.otherAmount = transformDecimal(closingCosts.otherAmount);
};

const modifyCovenants = covenants => {
  covenants.dscrFixedRate = covenants.dscrFixedRate ? covenants.dscrFixedRate / 100 : null;
  covenants.dscrTreasurySpread = covenants.dscrTreasurySpread ? covenants.dscrTreasurySpread / 100 : null;
  covenants.dscrFloatingRateSpread = covenants.dscrFloatingRateSpread ? covenants.dscrFloatingRateSpread / 100 : null;
  covenants.debtYieldAsIs = covenants.debtYieldAsIs ? covenants.debtYieldAsIs / 100 : null;
  covenants.debtYieldStabilized = covenants.debtYieldStabilized ? covenants.debtYieldStabilized / 100 : null;
  covenants.ltvAsIs = covenants.ltvAsIs ? covenants.ltvAsIs / 100 : null;
  covenants.ltvStabilized = covenants.ltvStabilized ? covenants.ltvStabilized / 100 : null;
  covenants.ltc = covenants.ltc ? covenants.ltc / 100 : null;
  covenants.occupancy = covenants.occupancy ? covenants.occupancy / 100 : null;
  covenants.suppFixedRateAssumption = covenants.suppFixedRateAssumption ? covenants.suppFixedRateAssumption : null;
};

export const modifyGuarantorRecourseSchedule = schedule => {
  const modifiedSchedule = { ...schedule };
  modifiedSchedule.recoursePercent = schedule.recoursePercent ? schedule.recoursePercent / 100 : null;
  modifiedSchedule.ltv = schedule.ltv ? schedule.ltv / 100 : null;
  modifiedSchedule.debtYield = schedule.debtYield ? schedule.debtYield / 100 : null;
  modifiedSchedule.occupancy = schedule.occupancy ? schedule.occupancy / 100 : null;

  return modifiedSchedule;
};

const modifyPrepaymentSchedules = prepaymentSchedules => {
  if (prepaymentSchedules) {
    for (let i = 0; i < prepaymentSchedules.length; i++) {
      const schedule = prepaymentSchedules[i];
      schedule.percent = schedule.percent ? schedule.percent / 100 : null;
      schedule.replacementRateSpread = schedule.replacementRateSpread ? schedule.replacementRateSpread / 10000 : null;
      clearIrrelevantFields(schedule);
    }
  }
};

const modifyRecourse = recourse => {
  if (recourse) {
    recourse.amount = recourse.amount ? recourse.amount / 100 : null;
    clearIrrelevantFields(recourse);
  }
};

const modifyHedgeRequirements = hedgeRequirements => {
  if (hedgeRequirements) {
    hedgeRequirements.maxCapStrike = hedgeRequirements.maxCapStrike ? hedgeRequirements.maxCapStrike / 100 : null;
    clearIrrelevantFields(hedgeRequirements);
  }
};

const modifyHedges = hedges => {
  if (hedges && hedges.length > 0) {
    hedges.forEach(hedge => {
      hedge.strike = hedge.strike ? hedge.strike / 100 : null;
      hedge.spread = hedge.spread ? hedge.spread / 100 : null;
      hedge.creditCharge = hedge.creditCharge ? hedge.creditCharge / 10000 : null;
    });
  }
};

const modifyExtensions = extensions => {
  if (extensions && extensions.length > 0) {
    extensions.forEach(extension => {
      extension.extensionLtv = extension.extensionLtv ? extension.extensionLtv / 100 : null;
      extension.extensionDebtYield = extension.extensionDebtYield ? extension.extensionDebtYield / 100 : null;
      extension.extensionFee = extension.extensionFee ? extension.extensionFee / 100 : null;
      extension.extensionAmortizationRate = extension.extensionAmortizationRate ? extension.extensionAmortizationRate / 100 : null;
    });
  }
};

const modifyProperties = properties => {
  if (properties && properties.length > 0) {
    properties.forEach(item => {
      item.allocationPercent = item.allocationPercent ? item.allocationPercent / 100 : null;
    });
  }
};

const clearIrrelevantFields = schedule => {
  switch (schedule.prepaymentTypeId) {
    case NONE:
      clearFields(schedule, ["openPeriod", "percent", "aggregateInterestMethodId", "aggregateInterestDefinedAmt", "aggregateInterestMonthsOfInterest"]);
      break;
    case LOCKOUT:
      clearFields(schedule, ["openPeriod", "percent", "aggregateInterestMethodId", "aggregateInterestDefinedAmt", "aggregateInterestMonthsOfInterest"]);
      break;
    case YIELD_MAINTENANCE:
    case YIELD_MAINTENANCE_AGENCY:
    case MAKE_WHOLE:
      clearFields(schedule, ["aggregateInterestMethodId", "aggregateInterestDefinedAmt", "aggregateInterestMonthsOfInterest"]);
      break;
    case FLAT_PERCENTAGE:
      clearFields(schedule, ["openPeriod", "aggregateInterestMethodId", "aggregateInterestDefinedAmt", "aggregateInterestMonthsOfInterest"]);
      break;
    case DEFEASANCE:
    case SPREAD_MAINTENANCE:
      clearFields(schedule, ["aggregateInterestMethodId", "aggregateInterestDefinedAmt", "aggregateInterestMonthsOfInterest"]);
      if (!schedule.shouldUsePV) {
        clearFields(schedule, ["replacementRateSpread", "lookbackDays", "roundingId"]);
      }
      break;
    case AGGREGATE_INTEREST:
      clearFields(schedule, ["percent", "openPeriod"]);
      break;
    default:
      break;
  }
};

const clearFields = (entity, fields) => {
  for (let i = 0; i < fields.length; i++) {
    entity[fields[i]] = null;
  }
};

export const findSummaryText = (data, field) => {
  if (!data) {
    return null;
  }
  const filtered = data.loanShorthandText.filter(x => x.field === field);
  let summaryText = null;
  if (filtered && filtered.length > 0) {
    summaryText = filtered[0].value;
  }

  return summaryText;
};

export const shouldShowField = (data, field) => {
  if (!data) {
    return null;
  }

  return get(data, field) !== null;
};

export const shouldShowArrayField = (data, field) => {
  if (!data || data?.field?.length === 0) {
    return null;
  }

  return get(data, field)?.length > 0;
};

export const isSysAdmin = data => data.authentication.isSystemAdmin;
